import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import api, { getApiErrorMessage } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import PaymentStep from './QuestionTypes/PaymentStep';
import { pickQuestionnaireLogoUrl } from '../utils/questionnaireBranding';
import './CheckoutPage.css';

const CheckoutPage = ({ token, clientConfig }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderSummary, setOrderSummary] = useState(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Payment config from checkout details (passed to PaymentStep in checkout mode)
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentConfigLoading, setPaymentConfigLoading] = useState(false);
  const paymentStepRef = useRef(null);

  // Coupon
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponValid, setCouponValid] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponRetryMode, setCouponRetryMode] = useState(false);

  // Pay
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);
  const [productImageFailed, setProductImageFailed] = useState(false);
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);

  const cacheTenantTrackingScripts = async (resolvedClientId) => {
    try {
      const clientId = resolvedClientId || orderSummary?.client_id || clientConfig?.id || localStorage.getItem('welliemd_client_id');
      const clientApiEndpoint = localStorage.getItem('welliemd_client_api_endpoint');
      const embedToken = localStorage.getItem('welliemd_embed_token');

      if (!clientId || !clientApiEndpoint || !embedToken) return;

      const url = `${clientApiEndpoint.replace(/\/$/, '')}/api/v1/clients/config/${clientId}/`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${embedToken}`,
          'Origin': window.location.origin,
        }
      });

      if (!response.ok) return;
      const payload = await response.json();
      const cfg = payload?.client_config || {};

      const conversionTag = typeof cfg.conversion_tracking_js === 'string' ? cfg.conversion_tracking_js.trim() : '';
      const globalTag = typeof cfg.custom_global_js === 'string' ? cfg.custom_global_js.trim() : '';

      if (conversionTag) sessionStorage.setItem('wellie_conv_tag', conversionTag);
      if (globalTag) sessionStorage.setItem('wellie_global_tag', globalTag);
    } catch (_) {
      // best-effort fallback only
    }
  };

  useEffect(() => {
    fetchCheckoutDetails();
  }, [token]);

  const applyPaymentConfig = (data) => {
    const pc = data?.payment_config || data?.paymentConfig || clientConfig || null;
    setPaymentConfig(pc || null);
  };

  const fetchCheckoutDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsTokenExpired(false);

      let retries = 0;
      const maxRetries = 10;
      while (!localStorage.getItem('welliemd_client_api_endpoint') && retries < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        retries++;
      }

      if (!localStorage.getItem('welliemd_client_api_endpoint')) {
        throw new Error('Client API endpoint not available. Please refresh the page.');
      }

      const data = await api.getCheckoutDetails(token);
      setOrderSummary(data);
      const totalFromApi = data?.total_amount ?? data?.amount;
      const discountFromApi = data?.discount_amount != null ? parseFloat(data.discount_amount) : 0;
      const originalFromApi = data?.original_price != null ? parseFloat(data.original_price) : null;
      if (discountFromApi > 0 || (originalFromApi != null && originalFromApi > 0)) {
        console.log('🎟️ Checkout details: discount applied at order creation', {
          discount_amount: data?.discount_amount,
          original_price: data?.original_price,
          total_amount: totalFromApi,
        });
      } else {
        console.log('🎟️ Checkout details: no discount in response', { total_amount: totalFromApi });
      }
      applyPaymentConfig(data);
      if (data?.client_id) {
        localStorage.setItem('welliemd_client_id', data.client_id);
      }
    } catch (err) {
      console.error('Failed to fetch checkout:', err);
      const errorMessage = getApiErrorMessage(err) || 'Invalid or expired checkout link.';
      if (errorMessage.toLowerCase().includes('expired') || errorMessage.toLowerCase().includes('not found')) {
        setIsTokenExpired(true);
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // The checkout payload can contain a payment-config snapshot from when the
  // checkout was created. Always refresh the live tenant config before showing
  // the payment form; a tenant may have changed processors while this checkout
  // link was still open.
  useEffect(() => {
    if (!orderSummary) return undefined;

    let mounted = true;
    setPaymentConfigLoading(true);
    api
      .getPaymentConfig()
      .then((res) => {
        const liveConfig = res?.data || res || null;
        if (!mounted) return;
        if (liveConfig?.active_gateway || liveConfig?.activeGateway) {
          applyPaymentConfig({ payment_config: liveConfig });
        }
      })
      .catch((err) => {
        // Keep the checkout snapshot as a fallback if the live refresh is
        // unavailable. The backend still validates the gateway at submit time.
        console.warn('Failed to refresh live payment config:', err);
      })
      .finally(() => {
        if (mounted) setPaymentConfigLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [orderSummary]);

  const handleRefreshToken = async () => {
    try {
      setRefreshing(true);
      setError(null);
      const refreshResult = await api.refreshCheckoutToken(token);
      if (refreshResult.checkout_url) {
        window.location.href = refreshResult.checkout_url;
      } else {
        throw new Error('Failed to get new checkout URL');
      }
    } catch (err) {
      console.error('Failed to refresh token:', err);
      setError(getApiErrorMessage(err) || 'Failed to refresh checkout link. Please restart your order.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRestartOrder = () => {
    window.location.href = window.location.origin;
  };

  const handleTryAnotherCoupon = () => {
    sessionStorage.removeItem('welliemd_coupon_code');
    sessionStorage.removeItem('welliemd_referral_campaign');
    sessionStorage.removeItem('welliemd_referral_source');
    setCouponCode('');
    setCouponValid(null);
    setCouponError(null);
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponRetryMode(true);
  };

  const handleCouponChange = (e) => {
    const value = e.target.value;
    setCouponCode(value);
    if (couponValid !== null || couponError || appliedCoupon || couponDiscount > 0) {
      setCouponValid(null);
      setCouponError(null);
      setAppliedCoupon(null);
      setCouponDiscount(0);
    }
  };

  const handleValidateCoupon = async () => {
    const code = couponCode?.trim();
    if (!code) {
      setCouponError('Please enter a coupon code');
      setCouponValid(false);
      return;
    }
    if (hasOrderCreationDiscount && !couponRetryMode) {
      setCouponError('You have already availed a discount.');
      setCouponValid(false);
      setAppliedCoupon(null);
      setCouponDiscount(0);
      return;
    }
    setCouponValidating(true);
    setCouponError(null);
    setCouponValid(null);
    try {
      const shippingFee = orderSummary?.primary_product?.shipping_fee != null
        ? parseFloat(orderSummary.primary_product.shipping_fee)
        : 0;
      const totalAmount = parseFloat(orderSummary?.total_amount ?? orderSummary?.amount ?? 0) || 0;
      const productSubtotal = orderSummary?.original_price != null
        ? parseFloat(orderSummary.original_price)
        : Math.max(0, totalAmount - shippingFee);
      const productId = orderSummary?.primary_product?.id ?? null;
      const result = await api.validateCoupon(code, productSubtotal, productId, token);
      if (result.valid) {
        const parseNum = (v) => {
          const n = Number.parseFloat(v);
          return Number.isFinite(n) ? n : null;
        };
        let resolvedDiscount =
          parseNum(result.discount) ??
          parseNum(result.discount_amount) ??
          parseNum(result.discountAmount) ??
          parseNum(result.amount_off) ??
          parseNum(result.discount_value) ??
          0;

        const couponValue = parseNum(result.value) ?? 0;
        if (resolvedDiscount <= 0 && (result.discount_type === "fixed" || result.discount_type === "amount")) {
          resolvedDiscount = couponValue;
        } else if (resolvedDiscount <= 0 && result.discount_type === "percent") {
          resolvedDiscount = (productSubtotal * couponValue) / 100;
        }
        resolvedDiscount = Math.max(0, Math.min(productSubtotal, resolvedDiscount));

        setCouponValid(true);
        setAppliedCoupon({
          code: code.toUpperCase(),
          type: result.discount_type,
          value: result.value,
          discount: resolvedDiscount,
        });
        setCouponDiscount(resolvedDiscount);
        setCouponError(null);
      } else {
        setCouponValid(false);
        setCouponError(result.message || 'Invalid coupon code');
        setAppliedCoupon(null);
        setCouponDiscount(0);
      }
    } catch (err) {
      setCouponValid(false);
      setCouponError(err.response?.data?.message || 'Failed to validate coupon');
      setAppliedCoupon(null);
      setCouponDiscount(0);
    } finally {
      setCouponValidating(false);
    }
  };

  const handlePay = async () => {
    if (!paymentStepRef.current || typeof paymentStepRef.current.getPaymentData !== 'function') {
      const msg = 'Payment form not ready. Please wait and try again.';
      setPayError(msg);
      toast.error(msg);
      return;
    }
    setPaying(true);
    setPayError(null);
    try {
      const data = await paymentStepRef.current.getPaymentData();
      const gateway = (data?.gateway || '').toLowerCase();
      const paymentData = data?.payment_data;

      // Mid-session gateway changes are allowed. Re-check live gateway right before pay
      // and force UI re-entry if gateway changed since this checkout page loaded.
      try {
        const liveCfg = await api.getPaymentConfig();
        const liveGateway = (
          liveCfg?.active_gateway ||
          liveCfg?.activeGateway ||
          ''
        ).toLowerCase();
        if (liveGateway && gateway && liveGateway !== gateway) {
          applyPaymentConfig({ payment_config: liveCfg });
          const msg = `Payment method changed to ${liveGateway.toUpperCase()}. Please re-enter your payment details.`;
          setPayError(msg);
          toast.error(msg);
          setPaying(false);
          return;
        }
      } catch (_) {
        // Non-blocking: backend will still protect with gateway mismatch guard.
      }

      const body = {};
      body.gateway = gateway;
      if (paymentData?.replace_payment_method_id) {
        body.replace_payment_method_id = paymentData.replace_payment_method_id;
      }
      if (gateway === 'stripe') {
        body.payment_method_id = paymentData?.payment_method_id || paymentData?.id || paymentData;
        if (paymentData?.postal_code) body.postal_code = paymentData.postal_code;
      } else if (gateway === 'nmi') {
        if (paymentData?.saved_payment_method_id) {
          body.saved_payment_method_id = paymentData.saved_payment_method_id;
        } else {
          body.payment_token = paymentData?.payment_token || paymentData?.token || paymentData;
        }
        if (paymentData?.card_meta) body.card_meta = paymentData.card_meta;
        if (paymentData?.postal_code) body.postal_code = paymentData.postal_code;
        if (paymentData?.cardholder_name) body.cardholder_name = paymentData.cardholder_name;
      } else if (gateway === 'authorize_net') {
        if (paymentData?.saved_payment_method_id) {
          body.saved_payment_method_id = paymentData.saved_payment_method_id;
        } else {
          body.opaque_data_descriptor = paymentData?.opaqueDataDescriptor || paymentData?.opaque_data_descriptor;
          body.opaque_data_value = paymentData?.opaqueDataValue || paymentData?.opaque_data_value;
          if (paymentData?.raw_card_data) {
            body.raw_card_data = true;
            body.card_number = paymentData.card_number;
            body.exp_month = paymentData.exp_month;
            body.exp_year = paymentData.exp_year;
            body.cvv = paymentData.cvv;
          }
        }
        if (paymentData?.postal_code) body.postal_code = paymentData.postal_code;
        if (paymentData?.cardholder_name) body.cardholder_name = paymentData.cardholder_name;
      }

      if (appliedCoupon?.code) body.coupon_code = appliedCoupon.code;

      const result = await api.checkoutPay(token, body);

      // Save conversion data for the thank you page BEFORE any redirect
      if (result.success) {
        try {
          const conversionData = {
            order_id: orderSummary?.business_order_id || orderSummary?.display_id || orderSummary?.id || '',
            order_uuid: orderSummary?.order_id || orderSummary?.id || '',
            amount: amountDueResolved.toFixed(2),
            product: displayName || '',
            coupon_code: appliedCoupon?.code || orderSummary?.applied_coupon?.code || '',
            buyer_name: sessionStorage.getItem('welliemd_buyer_name') ||
              orderSummary?.patient_name ||
              orderSummary?.patient?.name ||
              orderSummary?.customer_name ||
              '',
            buyer_email: sessionStorage.getItem('welliemd_buyer_email') || orderSummary?.patient_email || ''
          };
          sessionStorage.setItem('welliemd_conversion_data', JSON.stringify(conversionData));
          console.log('Conversion data saved to sessionStorage');
        } catch (e) {
          console.error('Failed to save conversion data:', e);
        }

        // Best-effort: cache tenant tracking scripts for thank-you fallback.
        const resolvedClientId =
          orderSummary?.client_id ||
          clientConfig?.id ||
          localStorage.getItem('welliemd_client_id') ||
          '';
        await cacheTenantTrackingScripts(resolvedClientId);
      }

      if (result.success && result.redirect_url) {
        window.location.href = result.redirect_url;
        return;
      }
      if (result.success) {
        const clientId =
          orderSummary?.client_id ||
          clientConfig?.id ||
          localStorage.getItem('welliemd_client_id') ||
          '';
        const thankYouPath = clientId ? `/${clientId}/thank-you` : '/thank-you';

        window.location.href = thankYouPath;
        return;
      }
      const errMsg = result.error || 'Payment failed.';
      setPayError(errMsg);
      toast.error(errMsg);
    } catch (err) {
      if (err?.response?.status === 409 && err?.response?.data?.code === 'gateway_changed') {
        const cfg = err?.response?.data?.payment_config;
        if (cfg) applyPaymentConfig({ payment_config: cfg });
      }
      const errMsg = getApiErrorMessage(err) || 'Payment failed.';
      setPayError(errMsg);
      toast.error(errMsg);
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <LoadingSpinner text="Retrieving your order..." />
    );
  }

  if (error) {
    return (
      <div className="checkout-container checkout-complete-state">
          <div className="checkout-complete-card">
            <div className="checkout-complete-icon" aria-hidden="true">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="30" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.2" />
                <path d="M20 32l8 8 16-16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
            </div>
            <h1 className="checkout-complete-title">You&apos;re all set</h1>
            <p className="checkout-complete-message">
              This checkout link is no longer active. If you already completed payment, thank you — your order is being processed.
              To place another order, please start from the beginning.
            </p>
            {/* Buttons commented out - not needed for this state
          <div className="checkout-complete-actions">
            {isTokenExpired && (
              <button
                type="button"
                onClick={handleRefreshToken}
                disabled={refreshing}
                className="checkout-complete-btn checkout-complete-btn-secondary"
              >
                {refreshing ? 'Refreshing...' : 'Refresh checkout link'}
              </button>
            )}
            <button
              type="button"
              onClick={handleRestartOrder}
              className="checkout-complete-btn checkout-complete-btn-primary"
            >
              Start new order
            </button>
          </div>
          */}
          </div>
      </div>
    );
  }

  const hasPaymentConfig = paymentConfig?.active_gateway || paymentConfig?.activeGateway;

  // Derive product + pricing details for the order summary card
  const primaryProduct = orderSummary?.primary_product || null;
  const displayName =
    primaryProduct?.product_name ||
    primaryProduct?.name ||
    (orderSummary?.items && orderSummary.items[0]?.name) ||
    'Order';
  const displayDescription =
    primaryProduct?.description ||
    '';
  const totalFromBackend = orderSummary?.total_amount ?? orderSummary?.amount;
  const rawAmount = totalFromBackend != null ? Number(totalFromBackend) : 0;
  const shippingFee =
    primaryProduct && primaryProduct.shipping_fee != null
      ? Number(parseFloat(primaryProduct.shipping_fee))
      : 0;
  const discountFromOrderCreation = orderSummary?.discount_amount != null ? parseFloat(orderSummary.discount_amount) : 0;
  const hasOrderCreationDiscount = discountFromOrderCreation > 0;
  const replacementCouponActive = Boolean(appliedCoupon && couponDiscount > 0);
  const visibleOrderCreationDiscount = replacementCouponActive ? 0 : discountFromOrderCreation;
  const showOrderCreationDiscount = visibleOrderCreationDiscount > 0;
  const originalPriceFromBackend = orderSummary?.original_price != null ? parseFloat(orderSummary.original_price) : null;
  const productOriginalPrice = primaryProduct?.original_price != null ? parseFloat(primaryProduct.original_price) : null;
  const productFinalPrice = primaryProduct?.final_price != null ? parseFloat(primaryProduct.final_price) : (primaryProduct?.total != null ? parseFloat(primaryProduct.total) - shippingFee : null) ?? (primaryProduct?.price != null ? parseFloat(primaryProduct.price) : null);
  const productHasListDiscount = productOriginalPrice != null && productFinalPrice != null && productOriginalPrice > productFinalPrice;
  const subtotalFromProduct = primaryProduct?.total != null ? parseFloat(primaryProduct.total) - shippingFee : null;
  const subtotalBeforeCoupon = originalPriceFromBackend != null && originalPriceFromBackend > 0
    ? originalPriceFromBackend
    : (subtotalFromProduct != null && subtotalFromProduct > 0 ? subtotalFromProduct : rawAmount + discountFromOrderCreation - shippingFee);
  const subtotal = Math.max(0, subtotalBeforeCoupon);
  const activeDiscount = replacementCouponActive ? couponDiscount : visibleOrderCreationDiscount;
  const amountDueResolved = Math.max(0, subtotal + shippingFee - activeDiscount);
  const linkedSupplies = (() => {
    if (Array.isArray(primaryProduct?.linked_supplies) && primaryProduct.linked_supplies.length > 0) {
      return primaryProduct.linked_supplies;
    }
    const itemSupplies = Array.isArray(orderSummary?.items)
      ? orderSummary.items.filter((it) => Boolean(it?.is_supply))
      : [];
    if (itemSupplies.length === 0) return [];
    return itemSupplies.map((it) => ({
      name: it?.name,
      quantity: it?.quantity || 1,
      is_included: Boolean(it?.is_included),
      unit_price: it?.price,
      shipping_fee: it?.shipping_fee,
    }));
  })();
  const getProductImageUrl = (rawImage) => {
    if (!rawImage || typeof rawImage !== 'string') return '';
    if (/^https?:\/\//i.test(rawImage) || rawImage.startsWith('data:')) {
      return rawImage;
    }

    const clientApiEndpoint = localStorage.getItem('welliemd_client_api_endpoint') || '';
    if (!clientApiEndpoint) return rawImage;

    try {
      const apiUrl = new URL(clientApiEndpoint);
      return new URL(rawImage, apiUrl.origin).toString();
    } catch (_error) {
      return rawImage;
    }
  };
  const checkoutItems = Array.isArray(orderSummary?.items)
    ? orderSummary.items.filter(Boolean).map((item, index) => {
        const kind = String(item?.kind || '').toLowerCase();
        const isSupply = Boolean(item?.is_supply) || kind === 'supply';
        const quantity = Number(item?.quantity || 1) || 1;
        const unitPrice = Number(item?.price ?? item?.final_price ?? item?.total ?? 0) || 0;
        const shippingFeeValue = Number(item?.shipping_fee || 0) || 0;
        return {
          id: item?.id ?? `item-${index}`,
          lineItemId: item?.line_item_id || null,
          name: item?.name || 'Item',
          description: item?.description || '',
          quantity,
          unitPrice,
          shippingFee: shippingFeeValue,
          isSupply,
          isIncluded: Boolean(item?.is_included),
          image: item?.product_image || '',
          parentLineItemId: item?.parent_line_item_id || null,
          sourceSupplyRelationId: item?.source_supply_relation_id || null,
        };
      })
    : [];

  const medicationGroups = [];
  const medicationGroupsByLineItemId = new Map();
  const orphanSupplyItems = [];
  checkoutItems.forEach((item) => {
    if (!item.isSupply) {
      const group = { ...item, supplies: [] };
      medicationGroups.push(group);
      if (item.lineItemId) {
        medicationGroupsByLineItemId.set(String(item.lineItemId), group);
      }
      return;
    }
    const currentMedication = item.parentLineItemId
      ? medicationGroupsByLineItemId.get(String(item.parentLineItemId))
      : medicationGroups[medicationGroups.length - 1];
    if (currentMedication) {
      currentMedication.supplies.push(item);
    } else {
      orphanSupplyItems.push(item);
    }
  });

  const orderSummaryPrimaryImage = medicationGroups[0]?.image || primaryProduct?.product_image || '';
  const productImageUrl = getProductImageUrl(orderSummaryPrimaryImage);
  const showProductImage = Boolean(productImageUrl) && !productImageFailed;
  const hasAnyLineItems = checkoutItems.length > 0;
  const checkoutMedicationSubtotal = checkoutItems
    .filter((item) => !item.isSupply)
    .reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
  const checkoutBillableSuppliesSubtotal = checkoutItems
    .filter((item) => item.isSupply && !item.isIncluded)
    .reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
  const checkoutShippingTotal = checkoutItems
    .reduce((sum, item) => sum + (item.shippingFee * item.quantity), 0);
  const checkoutSubtotalBeforeShipping = checkoutMedicationSubtotal + checkoutBillableSuppliesSubtotal;

  const branding = {
    ...(clientConfig?.questionnaire_config || clientConfig?.branding_config || clientConfig || {}),
    ...(orderSummary?.client_branding || {}),
  };
  const brandName = (
    branding.company_name
    || orderSummary?.client_name
    || clientConfig?.name
    || 'WellieMD'
  );
  let brandLogo = !brandLogoFailed ? pickQuestionnaireLogoUrl(branding) : '';
  if (brandLogo && brandLogo.includes('localstack')) {
    brandLogo = brandLogo.replace('localstack', 'localhost');
  }

  return (
    <div className="checkout-container">
        <header className="checkout-header">
          <div className="checkout-page__brand">
            {brandLogo ? (
              <img
                src={brandLogo}
                alt={brandName}
                onError={() => setBrandLogoFailed(true)}
              />
            ) : (
              <strong>{brandName}</strong>
            )}
          </div>
          <h1 className="checkout-title">Complete Your Checkout</h1>
          <p className="checkout-subtitle">Order #{orderSummary?.business_order_id || orderSummary?.display_id}</p>
        </header>

        <div className="checkout-content">
          <div className="checkout-main">
            {/* Show discount applied at order creation (referral/coupon from questionnaire) */}
            {showOrderCreationDiscount && (
              <section className="checkout-discount-banner">
                <p className="checkout-discount-banner-text">
                  Discount applied: −${visibleOrderCreationDiscount.toFixed(2)}
                  {orderSummary?.original_price && parseFloat(orderSummary.original_price) > 0 && (
                    <span className="checkout-discount-banner-original">
                      (original price ${parseFloat(orderSummary.original_price).toFixed(2)})
                    </span>
                  )}
                </p>
              </section>
            )}
            {/* Coupon toggle */}
            <div className="checkout-coupon-row">
              <button
                type="button"
                className="checkout-coupon-toggle"
                onClick={() => setCouponOpen(!couponOpen)}
              >
                Have a coupon code? {couponOpen ? '−' : '+'}
              </button>
            </div>

            {/* Order summary card */}
            <section className="checkout-card checkout-order-card">
              <div className="checkout-order-body">
                <h2 className="checkout-section-title">Order summary</h2>

                {hasAnyLineItems ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    {medicationGroups.map((medication, index) => {
                      const medicationTotal = (medication.unitPrice * medication.quantity) + (medication.shippingFee * medication.quantity);
                      return (
                        <div key={medication.id} className="checkout-order-product" style={{ alignItems: 'flex-start' }}>
                          {showProductImage && index === 0 ? (
                            <div className="checkout-order-product-icon">
                              <img
                                src={productImageUrl}
                                alt={medication.name}
                                className="checkout-order-product-image"
                                onError={() => setProductImageFailed(true)}
                              />
                            </div>
                          ) : (
                            <div className="checkout-order-product-icon" aria-hidden="true">
                              <span className="checkout-order-product-icon-pill" />
                            </div>
                          )}

                          <div className="checkout-order-product-info">
                            <div className="checkout-order-product-header">
                              <div>
                                <h3 className="checkout-order-product-name">{medication.name}</h3>
                                <p className="checkout-order-product-description">
                                  {medication.quantity > 1 ? `${medication.quantity} x ` : ''}
                                  Medication
                                  {medication.description ? ` • ${medication.description}` : ''}
                                </p>
                              </div>
                              <div className="checkout-order-product-price-block">
                                <span className="checkout-order-product-price checkout-order-product-price-final">
                                  ${medicationTotal.toFixed(2)}
                                </span>
                              </div>
                            </div>

                            {medication.supplies.length > 0 && (
                              <div className="checkout-linked-supplies">
                                <div className="checkout-linked-supplies-title">Supplies</div>
                                {medication.supplies.map((supply) => {
                                  const rowTotal = supply.isIncluded ? 0 : (supply.unitPrice * supply.quantity) + (supply.shippingFee * supply.quantity);
                                  return (
                                    <div className="checkout-linked-supply-row" key={supply.id}>
                                      <div className="checkout-linked-supply-name-wrap">
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                          <span className="checkout-linked-supply-name">{supply.name}</span>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.05em' }}>
                                            <span>x{supply.quantity}</span>
                                            <span className={`checkout-linked-supply-badge ${supply.isIncluded ? 'included' : 'addon'}`}>
                                              {supply.isIncluded ? 'Included' : 'Add-on'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      <span className="checkout-linked-supply-meta">
                                        {supply.isIncluded ? '$0.00' : `+$${rowTotal.toFixed(2)}`}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {orphanSupplyItems.length > 0 && (
                      <div className="checkout-linked-supplies">
                        <div className="checkout-linked-supplies-title">Supplies</div>
                        {orphanSupplyItems.map((supply) => {
                          const rowTotal = supply.isIncluded ? 0 : (supply.unitPrice * supply.quantity) + (supply.shippingFee * supply.quantity);
                          return (
                            <div className="checkout-linked-supply-row" key={supply.id}>
                              <div className="checkout-linked-supply-name-wrap">
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span className="checkout-linked-supply-name">{supply.name}</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.05em' }}>
                                    <span>x{supply.quantity}</span>
                                    <span className={`checkout-linked-supply-badge ${supply.isIncluded ? 'included' : 'addon'}`}>
                                      {supply.isIncluded ? 'Included' : 'Add-on'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <span className="checkout-linked-supply-meta">
                                {supply.isIncluded ? '$0.00' : `+$${rowTotal.toFixed(2)}`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="checkout-order-product">
                    {showProductImage ? (
                      <div className="checkout-order-product-icon">
                        <img
                          src={productImageUrl}
                          alt={displayName}
                          className="checkout-order-product-image"
                          onError={() => setProductImageFailed(true)}
                        />
                      </div>
                    ) : (
                      <div className="checkout-order-product-icon" aria-hidden="true">
                        <span className="checkout-order-product-icon-pill" />
                      </div>
                    )}
                    <div className="checkout-order-product-info">
                      <div className="checkout-order-product-header">
                        <div>
                          <h3 className="checkout-order-product-name">{displayName}</h3>
                          {displayDescription && (
                            <p className="checkout-order-product-description">{displayDescription}</p>
                          )}
                        </div>
                        <div className="checkout-order-product-price-block">
                          {productHasListDiscount && (
                            <span className="checkout-order-product-price-original" aria-hidden="true">
                              ${productOriginalPrice.toFixed(2)}
                            </span>
                          )}
                          <span className="checkout-order-product-price checkout-order-product-price-final">
                            ${(productHasListDiscount ? productFinalPrice : (primaryProduct?.price != null ? parseFloat(primaryProduct.price) : rawAmount)).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      {linkedSupplies.length > 0 && (
                        <div className="checkout-linked-supplies">
                          <div className="checkout-linked-supplies-title">Supplies</div>
                          {linkedSupplies.map((supply, index) => {
                            const name = supply?.name || "Supply";
                            const qty = Number(supply?.quantity || 1);
                            const included = Boolean(supply?.is_included);
                            const unitPrice = supply?.unit_price != null ? Number(supply.unit_price) : null;

                            let meta = "Included";
                            if (!included) {
                              const rowTotal = unitPrice !== null ? unitPrice * qty : null;
                              meta = rowTotal !== null ? `+$${rowTotal.toFixed(2)}` : "Priced";
                            }

                            return (
                              <div className="checkout-linked-supply-row" key={`supply_${index}`}>
                                <div className="checkout-linked-supply-name-wrap">
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <span className="checkout-linked-supply-name">{name}</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.05em' }}>
                                      <span>x{qty}</span>
                                      <span className={`checkout-linked-supply-badge ${included ? "included" : "addon"}`}>
                                        {included ? "Included" : "Add-on"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <span className="checkout-linked-supply-meta">{meta}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="checkout-order-breakdown">
                  {checkoutBillableSuppliesSubtotal > 0 ? (
                    <>
                      <div className="checkout-row">
                        <span>Medication Base</span>
                        <span>${checkoutMedicationSubtotal.toFixed(2)}</span>
                      </div>
                      <div className="checkout-row">
                        <span>Supplies</span>
                        <span>${checkoutBillableSuppliesSubtotal.toFixed(2)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="checkout-row">
                      <span>Subtotal</span>
                      <span>${checkoutSubtotalBeforeShipping.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="checkout-row">
                    <span>Shipping</span>
                    <span>${checkoutShippingTotal.toFixed(2)}</span>
                  </div>
                  {showOrderCreationDiscount && (
                    <div className="checkout-row checkout-row-discount">
                      <span>Coupon / Referral discount</span>
                      <span>- ${visibleOrderCreationDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  {couponDiscount > 0 && appliedCoupon && (
                    <div className="checkout-row checkout-row-discount">
                      <span>Coupon ({appliedCoupon.code})</span>
                      <span>- ${couponDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="checkout-row checkout-row-total">
                    <span>Amount due</span>
                    <span>${amountDueResolved.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Coupon input within card */}
              {couponOpen && (
                <div className="checkout-coupon-section">
                  {hasOrderCreationDiscount && !couponRetryMode ? (
                    <>
                      <div className="checkout-coupon-error checkout-coupon-already-availed">
                        You have already availed a discount.
                      </div>
                      <button
                        type="button"
                        className="checkout-coupon-try-another"
                        onClick={handleTryAnotherCoupon}
                      >
                        Try another coupon code
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="checkout-coupon-input-row">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={handleCouponChange}
                          placeholder="Enter coupon code"
                          disabled={couponValidating}
                          className={`checkout-coupon-input ${couponValid === true ? 'valid' : couponValid === false ? 'invalid' : ''
                            }`}
                        />
                        <button
                          type="button"
                          onClick={handleValidateCoupon}
                          disabled={couponValidating || !couponCode?.trim()}
                          className="checkout-coupon-apply"
                        >
                          {couponValidating ? '...' : 'Apply'}
                        </button>
                      </div>
                      {couponValid === true && appliedCoupon && (
                        <div className="checkout-coupon-success">
                          ✓ Coupon applied:{' '}
                          {appliedCoupon.type === 'fixed'
                            ? `$${appliedCoupon.value} off`
                            : `${appliedCoupon.value}% off`}
                        </div>
                      )}
                      {couponError && <div className="checkout-coupon-error">{couponError}</div>}
                    </>
                  )}
                </div>
              )}
            </section>

            {/* Payment card */}
            <section className="checkout-card checkout-payment-card">
              <div className="checkout-payment-header">
                <div>
                  <h2 className="checkout-section-title">Payment Method</h2>
                  <p className="checkout-payment-subtitle">
                    All transactions are secure and encrypted.
                  </p>
                </div>
              </div>

              {paymentConfigLoading && (
                <div className="checkout-no-gateway">
                  Checking the current payment processor...
                </div>
              )}

              {!hasPaymentConfig && !paymentConfigLoading && (
                <div className="checkout-no-gateway">
                  No payment gateway configured. Please contact support.
                </div>
              )}

              {hasPaymentConfig && !paymentConfigLoading && (
                <>
                  <PaymentStep
                    key={`checkout-payment-${paymentConfig.active_gateway || paymentConfig.activeGateway}`}
                    ref={paymentStepRef}
                    mode="checkout"
                    checkoutOrderSummary={orderSummary}
                    checkoutPaymentConfig={paymentConfig}
                    checkoutSavedPaymentMethods={orderSummary?.saved_payment_methods || []}
                    checkoutToken={token}
                    checkoutCouponDiscount={couponDiscount}
                    checkoutAppliedCoupon={appliedCoupon}
                    hideCheckoutOrderSummary={true}
                  />

                  {payError && (
                    <div className="checkout-pay-error">
                      {payError}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handlePay}
                    disabled={paying}
                    className="checkout-pay-button"
                  >
                    {paying ? 'Processing...' : 'Pay Now'}
                    <span className="checkout-pay-amount">
                      ${amountDueResolved.toFixed(2)}
                    </span>
                  </button>
                </>
              )}
            </section>
          </div>

          {/* Sidebar */}
          <aside className="checkout-sidebar">
            <div className="checkout-card checkout-help-card">
              <h4 className="checkout-help-title">Need Help?</h4>
              <p className="checkout-help-text">
                Contact our support team if you have any questions about your order.
              </p>
              {(orderSummary?.expires_at || orderSummary?.token_expires_at) && (
                <div className="checkout-help-footer">
                  Checkout expires:{' '}
                  {new Date(orderSummary.expires_at || orderSummary.token_expires_at).toLocaleString()}
                </div>
              )}
            </div>
          </aside>
        </div>
    </div>
  );
};

export default CheckoutPage;
